import { inject, Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";

import { NgxSpinnerService } from "ngx-spinner";

import { GastRequestModel, GastRequestWithBodyModel, HttpService, StorageService } from "gast-common-ng2";
import { Observable, Subject } from "rxjs";
import { CommonPortalUrlService } from "../url";
import { AuthenticationDto, IamLoginResponseDto, LoginDto, RegisterDto, UserDto } from "../../dto";

/**
 * Service which is responsible for all authorization actions in portal applications.
 *
 * WARNING: Don't use the UserDto for any Back-end calls. Its purpouse is only client-side presentation!
 */

export enum AuthOperation {
  Login,
  Logout,
  Register,
  Info,
}

export enum ResponseType {
  Success,
  Error,
}

export interface ServiceResponseDto {
  operation: AuthOperation;
  type: ResponseType;
  value: unknown;
}

const USER_KEY = "USER";
const SESSION_ID_KEY = "X-Session-Id";
const CHECK_SESSION_OBJ_AVAILABLE_PERIOD = 5_000;

@Injectable()
export class AuthService {
  userInfo = null;

  readonly #authServiceCompleted$$ = new Subject<ServiceResponseDto>();
  readonly #isSessionActive$$ = new Subject<boolean>();
  readonly #urlService = inject(CommonPortalUrlService);
  readonly #httpService = inject(HttpService);
  readonly #storageService = inject(StorageService);
  readonly #spinnerService = inject(NgxSpinnerService);

  constructor() {
    setTimeout(() => {
      this.getUserInfo(); // update the current UserInfo
      this.checkSessionObjectAvailable();
    }, 0);
  }

  checkSessionObjectAvailable(): void {
    setTimeout(() => {
      this.readUserInfo();
      this.checkSessionObjectAvailable();
    }, CHECK_SESSION_OBJ_AVAILABLE_PERIOD);
  }

  /** call login webservice */
  login(loginDto: LoginDto): void {
    const successCallback = (response: IamLoginResponseDto) => {
      const user =
        response && response.username
          ? this.saveUserObjectInStorage(response.username, response.fullname)
          : this.saveUserObjectInStorage("", "");

      // save sessionId in localstorage after login
      this.saveSessionIdInStorage(response.sessionId);

      this.#spinnerService.hide();
      this.#authServiceCompleted$$.next(this.#createResponseDto(AuthOperation.Login, ResponseType.Success, user));
    };

    const errorCallback = (error: HttpErrorResponse) => {
      console.log(error);
      this.#storageService.removeItem(USER_KEY);
      this.#spinnerService.hide();
      this.#authServiceCompleted$$.next(this.#createResponseDto(AuthOperation.Login, ResponseType.Error, error));
    };

    this.#spinnerService.show();
    this.#httpService.post(
      {
        mapper: (response: Response) => response.json(),
        callback: successCallback,
        url: this.#urlService.getLogin(),
        body: loginDto,
      } as GastRequestWithBodyModel<unknown, LoginDto>,
      errorCallback
    );
  }

  /** call logout webservice */
  logout(logoutUrl?: string): void {
    this.#spinnerService.show();
    this.#httpService.post({
      mapper: (response: Response) => response.json(),
      callback: (response: unknown) => {
        this.#storageService.removeItem(USER_KEY);
        this.#spinnerService.hide();
        this.#authServiceCompleted$$.next(
          this.#createResponseDto(AuthOperation.Logout, ResponseType.Success, response)
        );

        // if defined redirect
        if (logoutUrl) {
          window.location.href = logoutUrl;
        }
      },
      url: this.#urlService.getLogout(),
      body: null, // TODO: Is it correct that we send an empty body on logout?
    } as GastRequestWithBodyModel<unknown, null>);
  }

  /** call register webservice */
  register(registerDto: RegisterDto): void {
    const successCallback = () => {
      this.#spinnerService.hide();
      const loginDto = { username: registerDto.username, password: registerDto.password } as LoginDto;
      this.#authServiceCompleted$$.next(
        this.#createResponseDto(AuthOperation.Register, ResponseType.Success, loginDto)
      );
    };

    const errorCallback = (error: HttpErrorResponse) => {
      this.#spinnerService.hide();
      this.#authServiceCompleted$$.next(this.#createResponseDto(AuthOperation.Register, ResponseType.Error, error));
    };

    this.#spinnerService.show();
    this.#httpService.post(
      {
        mapper: (response: Response) => response.json(),
        callback: successCallback,
        url: this.#urlService.getRegister(),
        body: registerDto,
      } as GastRequestWithBodyModel<unknown, RegisterDto>,
      errorCallback
    );
  }

  /** get user info from webservice */
  getUserInfo(): void {
    this.#httpService.get({
      mapper: (response: Response) => response.json(),
      callback: (response: AuthenticationDto) => {
        const user =
          response && response.name
            ? this.saveUserObjectInStorage(response.name, response.principal.longusername)
            : this.saveUserObjectInStorage("", "");
        this.#authServiceCompleted$$.next(this.#createResponseDto(AuthOperation.Info, ResponseType.Success, user));
      },
      url: this.#urlService.getUserInfo(),
    } as GastRequestModel<unknown>);
  }

  /** read user info from local storage */
  readUserInfo(): UserDto {
    const userInfo = this.#storageService.getItem(USER_KEY) as UserDto;
    this.#checkSession(userInfo);
    return userInfo;
  }

  /** get the subject for completed callback */
  getAuthServiceResponse(): Observable<ServiceResponseDto> {
    return this.#authServiceCompleted$$.asObservable();
  }

  /** get the subject for completed callback */
  getSessionActiveObservable(): Observable<boolean> {
    return this.#isSessionActive$$.asObservable();
  }

  #checkSession(userInfo: UserDto): void {
    this.#isSessionActive$$.next(userInfo && !!userInfo.username);
  }

  saveUserObjectInStorage(username: string, fullname: string): UserDto {
    const user = { username: username, fullName: fullname } as UserDto;
    this.#storageService.setItem(USER_KEY, user);
    return user;
  }

  saveSessionIdInStorage(sessionId: string): string {
    this.#storageService.setItem(SESSION_ID_KEY, sessionId);
    return sessionId;
  }

  getSessionIdFromStorage(): string {
    return this.#storageService.getItem(SESSION_ID_KEY) as string;
  }

  getSessionIdKey(): string {
    return SESSION_ID_KEY;
  }

  /** create response object */
  #createResponseDto(operation: AuthOperation, type: ResponseType, value: unknown): ServiceResponseDto {
    return {
      operation,
      type,
      value,
    };
  }
}
